"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Submit = exports.Form = exports.SubmitState = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const app_1 = require("@obvio/app");
const utils_1 = require("@obvio/utils");
const react_1 = require("react");
const lib_1 = require("./lib");
const store_1 = require("./store");
const Button_1 = require("../Button");
const Toast_1 = require("../Toast");
const StyledHiddenForm = app_1.styled.form `
  pointer-events: none;
  position: absolute;
  width: 0;
  height: 0;
`;
var SubmitState;
(function (SubmitState) {
    SubmitState[SubmitState["OK"] = 0] = "OK";
    SubmitState[SubmitState["KEEP_LOADING"] = 1] = "KEEP_LOADING";
    SubmitState[SubmitState["DO_NOT_CLEAR"] = 2] = "DO_NOT_CLEAR";
})(SubmitState = exports.SubmitState || (exports.SubmitState = {}));
function Form({ id: passedId, loading, className, startOnPage, defaultValues, onSubmit, validate: formValidate, unloadLock: unloadLockCallback, contextOnly, children, onErrors, }) {
    const fallbackId = (0, app_1.useCuid)();
    const id = passedId !== null && passedId !== void 0 ? passedId : fallbackId;
    const loadingRef = (0, react_1.useRef)(loading);
    const mountedRef = (0, react_1.useRef)(false);
    const unloadLockRef = (0, react_1.useRef)(unloadLockCallback);
    const errorComponent = (0, react_1.useRef)(false);
    const [{ inputs, pages, aggregators, successPage, activePage, formError, submitting = false, maxPageReached, success, errors, }, dispatch,] = useInternalFormHook(id, {
        inputs: (0, lib_1.initDefaultInputs)(defaultValues !== null && defaultValues !== void 0 ? defaultValues : {}),
        pages: [],
        aggregators: {},
        submitting: !!loading,
        mountedRef,
        startPage: startOnPage,
    });
    (0, react_1.useEffect)(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    (0, react_1.useEffect)(() => {
        loadingRef.current = loading;
        dispatch('SET_STATE', { submitting: loading });
    }, [loading, dispatch]);
    const userTyped = Object.values(inputs.getState()).some(({ value }) => Boolean(typeof value === 'string' && value.length > 0));
    (0, react_1.useEffect)(() => {
        if (unloadLockRef.current) {
            if (userTyped) {
                utils_1.unloadLock.enable(typeof unloadLockRef.current === 'function'
                    ? unloadLockRef.current
                    : undefined);
            }
            else {
                utils_1.unloadLock.disable();
            }
        }
    }, [userTyped]);
    const registerFormError = (0, react_1.useCallback)(() => {
        if (errorComponent.current) {
            console.error(`<FormError> mounted more than once.`);
        }
        else {
            errorComponent.current = true;
        }
    }, []);
    const unregisterFormError = (0, react_1.useCallback)(() => {
        errorComponent.current = false;
        dispatch('SET_STATE', { formError: undefined });
    }, [dispatch]);
    const submitHandler = (0, react_1.useCallback)((ev) => __awaiter(this, void 0, void 0, function* () {
        ev === null || ev === void 0 ? void 0 : ev.preventDefault();
        if (submitting || (pages.length > 0 && successPage === activePage)) {
            return;
        }
        dispatch('SET_STATE', { submitting: true, formError: undefined });
        const submitTime = Date.now();
        let valid = true;
        const errorsList = [];
        const invalidAggregators = new Set();
        Object.entries(inputs.getState()).forEach(([name, { validate, value, error, aggregator, setError, unmounted, deactivated, },]) => {
            if (aggregator && !(aggregator in aggregators)) {
                return;
            }
            if (unmounted || deactivated) {
                return;
            }
            if (error) {
                errorsList.push(name);
                valid = false;
                if (aggregator) {
                    invalidAggregators.add(aggregator);
                }
            }
            else if (validate) {
                const newError = validate(value);
                if (typeof newError === 'string') {
                    setError(newError);
                    valid = false;
                    if (aggregator) {
                        invalidAggregators.add(aggregator);
                    }
                }
            }
        });
        if (errorsList.length > 0) {
            onErrors === null || onErrors === void 0 ? void 0 : onErrors(errorsList);
        }
        const { data, toAggregate, allData } = (0, lib_1.buildFormData)(inputs.getState());
        const normalizedAggregators = (0, lib_1.normalizeAggregators)(aggregators);
        const resolvedData = Object.assign({}, data);
        try {
            Object.assign(resolvedData, (0, lib_1.resolveAggregators)(normalizedAggregators, toAggregate, invalidAggregators));
        }
        catch (error) {
            valid = false;
            if (error instanceof Error &&
                error.message !== 'INVALID_AGGREGATOR_DATA') {
                console.error(error);
            }
        }
        if (valid) {
            if (!activePage || activePage === (0, utils_1.last)(pages)) {
                let stayInSubmittingState = false;
                try {
                    const formValidationErrors = formValidate === null || formValidate === void 0 ? void 0 : formValidate(resolvedData);
                    if (formValidationErrors !== undefined) {
                        Toast_1.toast.error(Object.values(formValidationErrors).join('\n'));
                        dispatch('SET_STATE', {
                            submitting: false,
                            errors: formValidationErrors,
                            formError: JSON.stringify(formValidationErrors),
                        });
                        return;
                    }
                    let submitErrors;
                    if (onSubmit) {
                        submitErrors = yield onSubmit(resolvedData, allData);
                    }
                    if (!submitErrors || typeof submitErrors === 'number') {
                        if (submitErrors === SubmitState.KEEP_LOADING) {
                            stayInSubmittingState = true;
                        }
                        else if (submitErrors !== SubmitState.DO_NOT_CLEAR) {
                            dispatch('CLEAR_FORM', true);
                        }
                    }
                    else {
                        if (!Array.isArray(submitErrors)) {
                            submitErrors = [submitErrors];
                        }
                        submitErrors.forEach((error) => {
                            if (typeof error === 'string') {
                                if (errorComponent.current) {
                                    dispatch('SET_STATE', { formError: error });
                                }
                                else {
                                    Toast_1.toast.error(error);
                                }
                            }
                            else {
                                Object.entries(error).forEach(([name, inputError]) => {
                                    const controller = inputs.getState()[name];
                                    if (!controller) {
                                        throw new Error(`Input "${name}" not registered in Form. You might be returning some object in "onSubmit" handler by mistake.`);
                                    }
                                    controller.setError(inputError);
                                });
                            }
                        });
                    }
                }
                catch (error) {
                    console.error(error);
                    const message = 'Something strange happened';
                    if (errorComponent.current) {
                        dispatch('SET_STATE', { formError: message });
                    }
                    else {
                        Toast_1.toast.error(message);
                    }
                }
                const deltaTime = Date.now() - submitTime;
                if (deltaTime < 100) {
                    yield (0, utils_1.delay)(undefined, 100 - deltaTime);
                }
                if (!stayInSubmittingState && !loadingRef.current) {
                    dispatch('SET_STATE', { submitting: false });
                }
            }
            else {
                const nextPageNumber = pages.indexOf(activePage) + 1;
                dispatch('SET_STATE', Object.assign({ maxPageReached: nextPageNumber + 1, activePage: pages[nextPageNumber] }, (loadingRef.current ? {} : { submitting: false })));
            }
        }
        else {
            const deltaTime = Date.now() - submitTime;
            if (deltaTime < 100) {
                yield (0, utils_1.delay)(undefined, 100 - deltaTime);
            }
            if (!loadingRef.current) {
                dispatch('SET_STATE', { submitting: false });
            }
        }
    }), [
        submitting,
        pages,
        successPage,
        activePage,
        inputs,
        aggregators,
        onErrors,
        formValidate,
        onSubmit,
        dispatch,
    ]);
    const formContext = (0, react_1.useMemo)(() => ({
        id,
        inputs,
        pages,
        registerFormError,
        unregisterFormError,
        formError,
        submitting,
        submit: submitHandler,
        activePage,
        successPage,
        maxPageReached,
        aggregators,
        success,
        errors,
    }), [
        id,
        inputs,
        pages,
        registerFormError,
        unregisterFormError,
        formError,
        submitting,
        submitHandler,
        activePage,
        successPage,
        maxPageReached,
        aggregators,
        success,
        errors,
    ]);
    const globalPortal = (0, app_1.useGlobalPortal)();
    const content = ((0, jsx_runtime_1.jsx)(lib_1.InternalFormContext.Provider, Object.assign({ value: formContext }, { children: (0, jsx_runtime_1.jsx)(lib_1.InternalFormIdContext.Provider, Object.assign({ value: id }, { children: (0, jsx_runtime_1.jsx)(lib_1.InternalFormDispatchContext.Provider, Object.assign({ value: dispatch }, { children: (0, jsx_runtime_1.jsxs)(lib_1.AggregateDataContext.Provider, Object.assign({ value: null }, { children: [globalPortal((0, jsx_runtime_1.jsx)(StyledHiddenForm, { id: id, onSubmit: submitHandler })), children] })) })) })) })));
    return contextOnly ? (content) : ((0, jsx_runtime_1.jsx)("div", Object.assign({ className: className }, { children: content })));
}
exports.Form = Form;
exports.Submit = (0, react_1.forwardRef)((_a, ref) => {
    var _b, _c;
    var { children, loading } = _a, props = __rest(_a, ["children", "loading"]);
    const context = (0, react_1.useContext)(lib_1.InternalFormContext);
    if (!context) {
        console.error('Critical: Submit button must be contained by the <Form> component.');
        return null;
    }
    const { id, submitting } = context;
    const disabled = (_c = (_b = props.disabled) !== null && _b !== void 0 ? _b : submitting) !== null && _c !== void 0 ? _c : undefined;
    const isLoading = Boolean(loading !== null && loading !== void 0 ? loading : submitting);
    return ((0, jsx_runtime_1.jsx)(Button_1.Button, Object.assign({}, props, { ref: (r) => {
            if (typeof ref === 'function') {
                ref(r);
            }
            else if (ref) {
                (0, utils_1.set)(ref, 'current', r);
            }
        }, form: id, type: "submit", disabled: disabled, loading: isLoading, "aria-label": "Submit" }, { children: children })));
});
function useInternalFormHook(id, _a) {
    var { inputs: inputDefaults } = _a, initialState = __rest(_a, ["inputs"]);
    const useStore = (0, store_1.getInternalFormStore)(id);
    if (useStore.getState().__detached) {
        useStore.setState(Object.assign(Object.assign({}, initialState), { __detached: undefined }));
        useStore.getState().inputs.setState((state) => (Object.assign(Object.assign({}, inputDefaults), state)));
    }
    return useStore('inputs', 'pages', 'aggregators', 'successPage', 'activePage', 'formError', 'submitting', 'maxPageReached', 'success', 'errors');
}
